import { observer } from "@/helpers/UI";
import { ref, onMounted, onBeforeUnmount, onUpdated, watch } from "vue";
import type { Ref } from "vue";
import type { Offset } from "@/models/common";
import type { MaybeElement } from "@vueuse/core";
import {
  invoke,
  until,
  useElementBounding,
  useElementSize,
  useParentElement
} from "@vueuse/core";

export const useTeleport = (
  targetElementRef: Ref<HTMLElement | null>,
  scrollable: string,
  type: string | null = "menu",
  textRef?: Ref<HTMLElement | null> | null,
  horizontalScrollable?: string | null,
  scrollUp = true
) => {
  const top = ref<number>(0);
  const left = ref<number>(0);
  const right = ref<number>(0);

  const disconnect = ref<(() => void) | null>(null);
  const assignPositionValues = () => {
    if (!targetElementRef.value) {
      return;
    }
    const bodyRect = document.body.getBoundingClientRect();
    const elemRect = targetElementRef.value.getBoundingClientRect();
    if (type === "menu") {
      top.value = elemRect.top - bodyRect.top + 25;
      left.value = elemRect.left - bodyRect.left;
      right.value = bodyRect.right - elemRect.right;
    }
    if (type === "toolTip" && textRef?.value) {
      top.value = elemRect.top - textRef.value.offsetHeight - 10;
      left.value = elemRect.left - 6;
      right.value = bodyRect.right - elemRect.right - 6;
    }
  };

  const attachPositionListener = () => {
    if (scrollable) {
      document
        .getElementById(scrollable)
        ?.addEventListener("scroll", assignPositionValues);
    }
    if (horizontalScrollable) {
      document
        .getElementById(horizontalScrollable)
        ?.addEventListener("scroll", assignPositionValues);
    }
    if (window) {
      window.addEventListener("resize", assignPositionValues);
    }
  };

  const detachPositionListener = () => {
    if (scrollable) {
      document
        .getElementById(scrollable)
        ?.removeEventListener("scroll", assignPositionValues);
    }
    if (scrollUp) {
      document.getElementById(scrollable)?.scrollBy(0, -1);
    }
    if (horizontalScrollable) {
      document
        .getElementById(horizontalScrollable)
        ?.removeEventListener("scroll", assignPositionValues);
      document.getElementById(horizontalScrollable)?.scrollBy(0, -1);
    }
    if (window) {
      window.removeEventListener("resize", assignPositionValues);
    }
  };

  watch(targetElementRef, assignPositionValues);

  onMounted(() => {
    assignPositionValues();
    attachPositionListener();

    disconnect.value = observer({
      elem1: scrollable,
      elem2: horizontalScrollable,
      cb: assignPositionValues
    });
  });

  onUpdated(() => {
    assignPositionValues();
  });

  onBeforeUnmount(() => {
    detachPositionListener();
    if (disconnect.value) {
      disconnect.value();
    }
  });

  return {
    top,
    left,
    right,
    assignPositionValues,
    attachPositionListener,
    detachPositionListener
  };
};
export interface UseTeleportV2Options<T extends HTMLElement = HTMLElement> {
  target: Ref<T | null>;
  to?: string;
  maintainSizeInOriginalPlace?: boolean;
  offset?: Offset;
}

/**
 * Utility to perform a teleport of an element which can't overflow elements up in the hierarchy
 *
 * @experimental This hook is not fully tested yet.
 * @param options
 * @returns void
 */
export const useTeleportV2 = <T extends HTMLElement>({
  target,
  to = "#modals",
  maintainSizeInOriginalPlace,
  offset
}: UseTeleportV2Options<T>) => {
  const initialCssPosition = ref<CSSStyleDeclaration["position"]>("");
  const reactiveParentElement = useParentElement(target);
  const originalParent = ref<typeof reactiveParentElement.value>(null);
  const { width: targetWidth, height: targetHeight } = useElementSize(target);
  const { top: parentTop, left: parentLeft } =
    useElementBounding(originalParent);

  const teleportParent = document.querySelector(to);
  const teleported = ref(false);

  const updatePosition = () => {
    if (!target.value || !originalParent.value) {
      return;
    }
    target.value.style.top =
      parentTop.value + (offset?.top ? offset.top : 0) + "px";
    target.value.style.left =
      parentLeft.value + (offset?.left ? offset.left : 0) + "px";
  };

  if (maintainSizeInOriginalPlace) {
    watch(
      [targetWidth, targetHeight],
      ([newWidth, newHeight]) => {
        if (!originalParent.value) {
          return;
        }
        originalParent.value.style.width = newWidth + "px";
        originalParent.value.style.height = newHeight + "px";
      },
      { immediate: true }
    );
  }

  invoke(async () => {
    await until(reactiveParentElement).toBeTruthy();
    originalParent.value = reactiveParentElement.value;
  });

  watch([parentTop, parentLeft], updatePosition, { immediate: true });

  watch(
    target,
    () => {
      if (!target.value || target.value.style.position === "absolute") {
        return;
      }
      if (!initialCssPosition.value) {
        initialCssPosition.value = target.value.style.position;
      }
      target.value.style.position = "absolute";
      if (!teleported.value) {
        if (teleportParent?.contains(target.value)) {
          teleportParent.removeChild(target.value);
        }
        teleportParent?.appendChild(target.value);
        teleported.value = true;
      }
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    if (!target.value) {
      return;
    }
    target.value.style.position = initialCssPosition.value;
    if (teleportParent?.contains(target.value)) {
      teleportParent.removeChild(target.value);
    }
    teleported.value = false;
  });

  onUpdated(updatePosition);
};

export const useElementBoundingLimited = (
  source: Ref<MaybeElement>,
  options?: { offsetLeft?: number; offsetTop?: number }
) => {
  const { top, left } = useElementBounding(source);
  const localTop = ref(top.value);
  const localLeft = ref(left.value);

  if (options) {
    watch([top, left], ([newTop, newLeft]) => {
      if (
        options.offsetLeft &&
        newLeft + options.offsetLeft > window.innerWidth
      ) {
        newLeft = window.innerWidth - options.offsetLeft;
      }
      if (
        options.offsetTop &&
        newTop + options.offsetTop > window.innerHeight
      ) {
        newTop = window.innerHeight - options.offsetTop;
      }
      localTop.value = newTop;
      localLeft.value = newLeft;
    });
  }

  return { top: localTop, left: localLeft };
};
